// src/Components/User/UserLogin.js
import React, { useState } from 'react';
import { 
  User, Lock, LogIn, AlertCircle, Eye, EyeOff, 
  ArrowRight, Sparkles, Crown, CheckCircle 
} from 'lucide-react';
// import './UserLogin.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

function UserLogin({ onLoginSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setLoginError('');
    
    try {
      const response = await fetch('https://api.truvz.com/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      // Store user data and token
      localStorage.setItem('userToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      
      // Call success callback
      if (onLoginSuccess) {
        onLoginSuccess(data.user);
      }
      
    } catch (err) {
      setLoginError(err.message);
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setFormData({
      email: 'demo@user.com',
      password: 'password123'
    });
  };

  return (
    <div className="user-login-container">
      <div className="login-wrapper">
        <div className="login-header">
          <div className="login-logo">
            <Crown size={48} />
            <h1>Visa Application Portal</h1>
          </div>
          <p>Welcome back! Please login to access your assigned applications</p>
        </div>
        
        <div className="login-form-container">
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <User size={18} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
              </div>
              {errors.email && (
                <div className="error-message">
                  <AlertCircle size={14} />
                  {errors.email}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper password-input">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className={errors.password ? 'error' : ''}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <div className="error-message">
                  <AlertCircle size={14} />
                  {errors.password}
                </div>
              )}
            </div>
            
            {loginError && (
              <div className="login-error">
                <AlertCircle size={18} />
                {loginError}
              </div>
            )}
            
            <button
              type="submit"
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Logging in...
                </>
              ) : (
                <>
                  <LogIn size={18} />
                  Login to Dashboard
                </>
              )}
            </button>
          </form>
          
          <div className="login-options">
            <div className="divider">
              <span>OR</span>
            </div>
            
            <button
              type="button"
              className="demo-login-button"
              onClick={handleDemoLogin}
            >
              <Sparkles size={18} />
              Try Demo Account
            </button>
          </div>
          
          <div className="login-footer">
            <p>
              <CheckCircle size={16} />
              Your data is securely protected
            </p>
            <p className="help-text">
              Need help? Contact support at <a href="mailto:support@visaapplication.com">support@visaapplication.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserLogin;